/**
 * FreeWorldGuard - Blocks Free World access when:
 * 1. freeworld_enabled=false in app_control
 * 2. User has admin role (admins should use teacher account to play)
 * 
 * Shows a friendly message instead of crashing or redirecting.
 * 
 * Dev override: Set VITE_ALLOW_ADMIN_PLAY=true to allow admin to play (testing only)
 */
import { useAppControl } from '@/hooks/useAppControl';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Globe, ArrowLeft, RefreshCw, ShieldAlert } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { useIsClassroomDemoPlayer } from '@/hooks/useIsClassroomDemoPlayer';

// Dev override for admin testing - default false
const ALLOW_ADMIN_PLAY = import.meta.env.VITE_ALLOW_ADMIN_PLAY === 'true';

interface FreeWorldGuardProps {
  children: React.ReactNode;
}

export function FreeWorldGuard({ children }: FreeWorldGuardProps) {
  const { data: appControl, isLoading: appControlLoading, refetch } = useAppControl();
  const { hasRole, loading: rolesLoading } = useUserRoles();
  const navigate = useNavigate();

  const isLoading = appControlLoading || rolesLoading;
  const isAdmin = hasRole('admin');

  // Show loading while checking
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Block admin users from playing (unless dev override is enabled)
  if (isAdmin && !ALLOW_ADMIN_PLAY) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="flex justify-center mb-6">
            <ShieldAlert className="h-16 w-16 text-amber-500" />
          </div>
          
          <h1 className="text-xl font-bold mb-3 text-foreground">
            Olet kirjautunut admin-tilillä
          </h1>
          
          <p className="text-muted-foreground mb-6">
            Admin-tilillä pelaaminen on estetty, koska se aiheuttaa ongelmia pelaajatietojen 
            omistajuussuhteissa. Pelaa opettaja- tai opiskelijatilillä.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Admin-hallintapaneeliin
            </Button>
            <Button 
              variant="default"
              onClick={() => navigate('/opettaja')}
              className="gap-2"
            >
              Siirry opettajanäkymään
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground mt-6">
            💡 Vinkki: Käytä eri selainta tai incognito-tilaa testaamiseen
          </p>
        </Card>
      </div>
    );
  }

  // If Free World is disabled, show friendly message
  if (appControl && !appControl.freeworld_enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="flex justify-center mb-6">
            <Globe className="h-16 w-16 text-muted-foreground opacity-50" />
          </div>
          
          <h1 className="text-xl font-bold mb-3 text-foreground">
            Loppuhaaste on tilapäisesti pois käytöstä
          </h1>
          
          <p className="text-muted-foreground mb-6">
            {appControl.banner_enabled && appControl.banner_message 
              ? appControl.banner_message 
              : 'Loppuhaaste-ominaisuus on tilapäisesti poissa käytöstä huoltotöiden vuoksi. Voit jatkaa muiden osioiden käyttöä.'}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/student')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Takaisin etusivulle
            </Button>
            <Button 
              variant="ghost"
              onClick={() => refetch()}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Tarkista uudelleen
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Free World is enabled and user is not admin, render children
  return <>{children}</>;
}
